import React, { useEffect, useRef, useState } from 'react';
import CSidebarButtonBase, { type CSidebarButtonBaseProps } from './dashboardSidebar/CSidebarButtonBase';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import { useAppSelector } from '../hooks/hooks';
import { updateStateAttribute } from '../helpers/helper';

interface CSidebarGroupButtonProps extends Omit<CSidebarButtonBaseProps, 'endIcon' | 'children'> {
  title: JSX.Element | string;
  children: JSX.Element | JSX.Element[];
  groupIsActive: boolean;
}

interface State {
  isExpanded: boolean;
  isHovered: boolean;
  isLoaded: boolean;
  childrenHeight: number;
}

export default function CSidebarGroup(props: CSidebarGroupButtonProps) {
  const {dashboardOutletDimensions, headerDimensions, sidebar} = useAppSelector((state) => state.dashboard);
  const parentButton = useRef<HTMLDivElement>(null);
  const ghostWrapper = useRef<HTMLDivElement>(null);
  const [state, setState] = useState<State>({childrenHeight: 0, isExpanded: true, isHovered: false, isLoaded: false});

  useEffect(() => {
      const measureWrapperHeight = () => {
        return ghostWrapper.current?.getBoundingClientRect().height ?? 0;
      };
      updateStateAttribute<State>({ childrenHeight: measureWrapperHeight()}, setState);
      setTimeout(() => { updateStateAttribute<State>({ isLoaded: measureWrapperHeight() > 0 }, setState); }, 1000);
  }, [dashboardOutletDimensions, state.isExpanded]);

  const handlePopupClick = () => {
    updateStateAttribute<State>({isHovered: false}, setState);
  };

  const fadeSlideStyle = (visible: boolean): React.CSSProperties => ({
    opacity: visible ? 1 : 0,
    transform: visible ? 'translateX(0px)' : 'translateX(-8px)',
    transition: 'opacity 400ms ease, transform 400ms ease'
  });

  const popupVisible = (!sidebar.isExpanded || !state.isExpanded) && state.isHovered;

  return (
    <div ref={parentButton}
         className={'flex flex-col'}
         // onClick={() => { updateStateAttribute<State>({isHovered: false}, setState); }}
         onMouseEnter={() => { updateStateAttribute<State>({isHovered: true}, setState); }} onMouseLeave={() => { updateStateAttribute<State>({isHovered: false}, setState); }}>
      {/* Group button */}
      <div className={'text-white flex-grow'}>
        <CSidebarButtonBase {...props} stopPropagation={headerDimensions.narrowScreen}
                            isActive={props.groupIsActive && (!sidebar.isExpanded || !state.isExpanded)}
                            endIcon={{
                              default: <span className={'absolute -right-1 w-5'}><ChevronRightIcon style={{
                                transform: state.isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                                transition: 'transform ease 300ms',
                              }}/></span>}}
                            onClick={() => {
                              if (!sidebar.isExpanded) return;
                              updateStateAttribute<State>({ isExpanded: !state.isExpanded, isHovered: false }, setState);
                            }}>{props.title}</CSidebarButtonBase>
      </div>

      {/* Sidebar items */}
      <div className={'flex flex-col gap-1 overflow-hidden text-white'}
           style={{
             height: state.isExpanded ? state.childrenHeight : 0,
             transition: state.isLoaded && state.isExpanded ? 'height ease 300ms' : 'none',
             display: sidebar.isExpanded ? 'flex' : 'none' }}>
        <div/>
        {props.children}
      </div>

      {/* Pop-up menu */}
      <div className={'px-4 flex flex-col gap-1 text-inherit absolute left-[calc(100%-0.5rem)] sidebar-dropdown z-50'}
           style={{
             top: (parentButton.current?.getBoundingClientRect().top ?? 0) - headerDimensions.h - 12,
             pointerEvents: popupVisible? 'auto' : 'none',
             ...fadeSlideStyle(popupVisible)
           }}
           onClick={handlePopupClick}>
        <div className={'p-2 border rounded-2xl bg-white shadow-lg text-gray-700'}>
          <div className={'flex flex-col gap-1'} ref={ghostWrapper}>
            <div />
            {props.children}
          </div>
        </div>
      </div>
    </div>
  );
}
